/**
 * Set Use Case Command
 *
 * Sets the selected use case for the genogram workflow.
 * This command follows the Command pattern with full undo/redo support.
 */

import { BaseCommand } from '../../../core/commands/ICommand';
import { UseCaseType } from '../models/UseCase';
import { getWorkflowMutationManager, WorkflowChangeRecord } from '../../../core/commands/mutations/WorkflowMutationManager';
import { inferAssistantRoleFromUseCase } from '../models/AssistantRole';
import { getDefaultViewpointForRole } from '../models/AssistantRolePolicy';
import { usePerspectiveStore, type Perspective } from '../../../stores/perspectiveStore';

export class SetUseCaseCommand extends BaseCommand {
  private changeRecord: WorkflowChangeRecord | null = null;
  private previousPerspective: Perspective | null = null;

  constructor(private useCaseId: UseCaseType | null) {
    super('Set Use Case', useCaseId ? `Set use case to ${useCaseId}` : 'Clear use case selection');
  }

  execute(): void {
    const manager = getWorkflowMutationManager();

    // Reset workflow when changing use case
    manager.resetWorkflow();

    // Infer assistant role from starter use case/template choice.
    const inferredRole = inferAssistantRoleFromUseCase(this.useCaseId);

    // Set use case + inferred role together so undo restores both.
    this.changeRecord = manager.updateWorkflow({
      selectedUseCase: this.useCaseId,
      assistantRole: inferredRole,
    });

    const nextPerspective = getDefaultViewpointForRole(inferredRole);
    if (nextPerspective) {
      const perspectiveState = usePerspectiveStore.getState();
      this.previousPerspective = perspectiveState.perspective;
      perspectiveState.setPerspective(nextPerspective);
    }

    console.debug('[SetUseCaseCommand] Executed', { useCaseId: this.useCaseId, inferredRole });
  }

  undo(): void {
    if (!this.changeRecord) return;

    const manager = getWorkflowMutationManager();
    manager.updateWorkflow(this.changeRecord.oldState);
    if (this.previousPerspective) {
      usePerspectiveStore.getState().setPerspective(this.previousPerspective);
    }

    console.debug('[SetUseCaseCommand] Undone');
  }
}
